<template>
  <VaLayout
    class="test"
    :class="themeClass"
    :top="{ fixed: true, order: 2 }"
    :left="{ fixed: true, absolute: breakpoints.mdDown, order: 1, overlay: breakpoints.mdDown && !isSidebarMinimized }"
    @leftOverlayClick="isSidebarMinimized = true"
  >
    <template #top>
      
      <AppNavbar :is-mobile="isMobile" />
    </template>

    <template #left>
      <AppSidebar :minimized="isSidebarMinimized" :animated="!isMobile" :mobile="isMobile" class="mt-4" />
    </template>

    <template #content>
      <div :class="{ minimized: isSidebarMinimized }" class="app-layout__sidebar-wrapper">
        <div v-if="isFullScreenSidebar" class="flex justify-end">
          <VaButton class="px-4 py-4" icon="md_close" preset="plain" @click="onCloseSidebarButtonClick" />
        </div>
      </div>
      <AppLayoutNavigation v-if="!isMobile" class="p-4" />
      <main class="p-4 pt-0 mt-4">
        <article>
          <Transition name="fade">
            <RouterView />
          </Transition>
        </article>
      </main>
    </template>
  </VaLayout>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { onBeforeRouteUpdate } from 'vue-router'
import { useBreakpoint } from 'vuestic-ui'
import { useColors } from 'vuestic-ui'
import { useGlobalStore } from '../stores/global-store'

import AppLayoutNavigation from '../components/app-layout-navigation/AppLayoutNavigation.vue'
import AppNavbar from '../components/navbar/AppNavbar.vue'
import AppSidebar from '../components/sidebar/AppSidebar.vue'

const GlobalStore = useGlobalStore()
const { currentPresetName } = useColors();
const breakpoints = useBreakpoint()

const sidebarWidth = ref('16rem')
const sidebarMinimizedWidth = ref(undefined)

const isMobile = ref(false)
const isTablet = ref(false)
const { isSidebarMinimized } = storeToRefs(GlobalStore)

const onResize = () => {
  isSidebarMinimized.value = breakpoints.mdDown
  isMobile.value = breakpoints.smDown
  isTablet.value = breakpoints.mdDown
  sidebarMinimizedWidth.value = isMobile.value ? '0' : '4.5rem'
  sidebarWidth.value = isTablet.value ? '100%' : '16rem'
}

onMounted(() => {
  window.addEventListener('resize', onResize)
  onResize()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})

onBeforeRouteUpdate(() => {
  if (breakpoints.mdDown) {
    // Collapse sidebar after route change for Mobile
    isSidebarMinimized.value = true
  }
})

const isFullScreenSidebar = computed(() => isTablet.value && !isSidebarMinimized.value)

const themeClass = computed(() => `theme-${currentPresetName.value}`);

const onCloseSidebarButtonClick = () => {
  isSidebarMinimized.value = true
}
</script>

<style lang="scss" scoped>
// Prevent icon jump on animation
.va-sidebar {
  width: unset !important;
  min-width: unset !important;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.theme-light {
  background-image: url('../../public/light_bg.jfif');
}

.theme-dark {
  background-image: url('../../public/dark_bg.jfif');
}

.test {
// background-image: url(data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wCEAAkGBw0NDQ0NDQ0HBwgHBw0HBwcHCA8IDQcNFREWFhURExMYHSggGBolJxMTITEhJSkrLi4uFx8zODMsNygtLisBCgoKDQ0NDw8NDysZFRkrKzc3NzcrKystLSsrNy0rLSstNy0rKysrKysrLSsrKysrKysrKysrKysrKysrKysrK//AABEIALcBEwMBIgACEQEDEQH/xAAbAAEBAQEBAQEBAAAAAAAAAAACAQADBgcFBP/EABgQAQEBAQEAAAAAAAAAAAAAAAABEQIS/8QAGQEBAQEBAQEAAAAAAAAAAAAAAQACBQME/8QAFxEBAQEBAAAAAAAAAAAAAAAAABEBEv/aAAwDAQACEQMRAD8A91CkaRX3vkxpCjSECyxCkBWRY0hRNMrYsgLFGhRnS0KRJCkDSwokKM6VhQYcZ1LCglGNaWEMJlKqRQVKCo0qya2g4S6jDSWro62hqHraLaFD1gZKPLSLGwo6jnNFjSFIi0hRpCkBbFkaQsDTSLjSKzulpCjSLAVhRpFg1poUZYyWhRIQ1KsZYwWiswKxUisnFZGDRKOsjmEw6ugwtbRYNZh62hraGoesGsFHnpFkbCkdRzGkKRJDkBaRZGwpAcaRVkWRnWsaLIsiyBNiyNIUiaxsKRpFxktFWRgWhNITOpFZQWZmZKsyJKya2iN4Wtoa2iNHraGtoaw9bQ1tDWHqaOpojeYeoGsDH5GFjLI6TkLIWNIsgaaQo0hSDS0iyLIuMlMKRpCkRSQpFkKRlpMXFXAUxcbFSaEkVlMzMEzMwaZGTUm1LWqJrF1NTU0N4Wto6miNYetoa2iNYWtoek9DceuHqBrBuP45CkaQpH3uK0hSNIsjO600hSNCkBaRZFiotIsjSKyVhRIoLKypIysizMwSsyBKmtURZNS0bUSS0fQ2qNYVqaNqaI3h6mudraI1h6loek9CNYepaHpL0I9cP0jn6YR6LIUiyLI+txWkWRZFwFpCkaQpA0mE2LiLLGxZAVjKwTMrIoyokzMiSpa1o1JbRtS0bQato2paNpiK0bRtG1QnaOud6S9KN5p63pyvSelGs119D6cr0l6EazXW9DenK9jexHrmuvtnD2g5b6fqSLIUi49XJSRZFxZFS0hY0hSApi4uLICkiqyLMzJMzJqTNalqWpLaOjalqVW9JehtC0xU7QtG9DejFStG0b0F6UVO9BehvQ2mGlaloXob0o1SvQ3obQvSjWad6G9ud6G9KNZrpexvblehvSjWa7e0cfTLlrp6WRcLFxmvhg4uFi4CkixpFRZmZFWRklbUSoVrUtRKRWtS1KNqiq2jalo2kVrRtS0LTFStC1LQtMVW0b0NoXoxUr0No3obTDStG9BaN6UNO9BehvQ2qNZq2hek66c70Y1SvQ3obUtUNP0zlrGGvbY2HjY+d4Bi4WNiQ4uLjJCypUkZk1JkrJaQlSpaNpFW0LWtG0pLRta0LTmBrQtbrpztaiW0bUvQWmBrRtS0LSltG9DehtUNK9BehtHSaVoXprQtUaq2halo2mNVbR0bU1Q09YNYxV9BxsNMfFRAYrBwiIypUBSrRpSVKto1Bhta0bTga0LWtG1oNaFrWhaU1odVuq59VoNaFrWhaQtoWpenO9GJeqF6S0LWoltG1LQtRO0LRvQ3oxUr0FqWh1TGltG1LRphpWpo6mqGlrDrJV9MsGx1sGxzno5jYdg04Ao06FaZGjSoUqJaNq0LUGtC1rQtaDWha1odVrA16C9NaFrQa1ztW1ztIa0Oq1odUhOqFrWj1Wklo2tRtKa0LWtC05gW0ampaS1o2taNJq2jampqVXUtaiTVZNZJ9UsCxWct7udg1GOIaFZmgFGszQChWY4AtCoxAWh0zNYAoWszQChazEOdDpmawBQ6RmglCsxwaNoVmaQ0azJDaNrMUmprMkmprMSjMyT/2Q==);
// background-image: url(data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wCEAAkGBwgICAgICAgHBwcHBwoHBwcHBw8ICQcKFREWFhURHxMYHSggGCYlGxMTITEhJSkrLi4uFx8zODMsNygtLisBCgoKDQ0NDw0NFSsZFRkrKysrNy0rKys3KystKzctKy0rKy03KysrKy0rKysrKysrKy0tKysrKysrKysrKysrLf/AABEIALcBEwMBIgACEQEDEQH/xAAbAAEBAQEBAAMAAAAAAAAAAAAAAQIDBgQFB//EABcQAQEBAQAAAAAAAAAAAAAAAAABEQL/xAAaAQADAQEBAQAAAAAAAAAAAAABAgMABgQF/8QAGREBAQEBAQEAAAAAAAAAAAAAAAECERIh/9oADAMBAAIRAxEAPwD8TxcXDHs4yDWJhuAyqg+SsjWGD5DqCg+W6mGLi4Plusqo3kEMUN5ZMXBcHy3UFwweAguGNxkFwHjIKgcYRQOCgqAyI1UJRQClogBWdMMbwxWZV4xhjeJhvJbGUxvEN5LWRcDeSIKo+QZVcMHwyYLi43kEFwHyyCjeW6go3GQUbjIKgcYRQOMiKUooilLRRFQlZACUwAVnycMawx65l6eM4Y1hh5ktjGJjpiYaZTsc7EdLEsNMpajJirh/JGVxcMbyyYYo3lkwxQPLIKBxkFA4yIoDIKgVkFQtFKKhKKFCkooioSshQTpgQBnzsMbwx9GZe3jGLjWLikyFjGJjphhplOxzxm8uuJYaZR1lyxMdLyzhvKGpxlVwbyVBQLGZFC8FBUDjCKhWEVC0RFQtYRUJREVKSiiKhKJUEStEqKhLRBAnRfa4Y3IuPtzL6PljDG8XDzIWMYY3hhuJ3LniWOuJg8S1lysYvLtYzYPHn1lxxHTrljB489+IKhbGRGkJYKAUrIASiiLUJREqoSsIBKKICdoiURO0RConaYRUTtEECs+9wxvDHRSPr8Yxcaww8hbEwxrDB4nYxiWOmJYPEtRzsZsdLGbG4hqOVjn1Hexz6jPPvLki1AsQRFKnRZConREVCURBE7REBO0RBE7REBO0UBE7REETtERUIIADPRYY3hjp5H3LGMMbMPwljOGNDJ2M4ljSVktRixmx0rNBHUc7GOo6VjoENZcOow7dRxoV5dTlRFROliIqJ0yJVRK0RBErREoidoiAlaIgidoiAS0UBCWiAhawAAvUYmN4mOrj71jJigksQVGTsRGqyyWolZrVZpUdRisdN1ihUdRz6cenbpy6J15txzRayS1ESiI6pioIlaJUERtEBE7REETtEQE7REAlFAC1kAKIAzPVio6x0VjItQydRKqAlqIlEoJWJWatrNC1LUZrFarFqdqOoz059N9OfVJa8+o59MVvpzpLULBKIjqgJQqOqZARK0RBErREVCWiICdoiAWsIACAgMAAz1qLUrrOujqVmrUo9TqVCs1up0rNpazaW1KlrNpazaS1Gpax1VtYtJajpKxVtYqdqOoz0xW6xU7pDUZColqlEERtERUStEQRO0RAJaIgFYRUKIADCADADM9ZaialrqeuitLWaWs2h1OraxaWs2t1K0rNpazaW6TtLWLS1m0l0jaWsWrazanalpKxVtZqd0lUrNWs1O6SsSsrUTtJxARO1uCCJWiIqJ2iIqFrAIUQAGEAGAAYABnqNZtB03X36lrNoB1Os2s2gHU6xazaoXqVYtZ1AtqdS1mgnanWazQJU6lZoJ1Os1KCdJWRBOgICdFACMgAMgBRAAZAGYAKwAwv/9k=);
// background-image: ;
background-size: cover;     // Растягивает, сохраняя пропорции, закрывая всю область
background-position: center; // Центрирует изображение
background-repeat: no-repeat;
transition: background-image 1.3s ease;
}
</style>
